import { NestedTreeControl } from '@angular/cdk/tree';

import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { TranslateService } from '@ngx-translate/core';
import { filter, finalize, forkJoin, iif, takeUntil } from 'rxjs';
import { ICriterion } from '../../distribute-team-members/models/criterion';
import { BaseComponent } from '../base.component';
import { CategoriesCompliance } from '../models/categories-compliance.model';
import { ExecuteVisitType } from '../models/execute-visit.model';
import { MemberModel } from '../models/member.model';
import { RolesEnum, VisitMetaData } from '../models/visit-metadata.model';
import { CriterionsService } from '../services/criterions.service';
import _cloneDeep from 'lodash/cloneDeep';
import { ItimadCriterionsService } from '../services/itimad-criterions.service';


@Component({
  selector: 'app-criterions-tree',
  templateUrl: './criterions-tree.component.html',
  styleUrls: ['./criterions-tree.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CriterionsTreeComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() viewOnly: boolean;
  @Input() categoriesCompliance: CategoriesCompliance[];
  @Input() visitMetaData: VisitMetaData;
  @Input() sessionId: number | string;
  @Input() executeVisitType: ExecuteVisitType;
  criterions: ICriterion[] = [];
  summaries = [
    {
      type: 'recommendations-summaries',
      filter: 'summaryOfRecommendations',
      label: 'forms.common.Criteria.summaryOfRecommendations'
    },
    {
      type: 'commendations-summaries',
      filter: 'summaryOfCommendations',
      label: 'forms.common.Criteria.summaryOfCommendations'
    },
    {
      type: 'suggestions-summaries',
      filter: 'summaryOfSuggestions',
      label: 'forms.common.Criteria.summaryOfSuggestions'
    },
    {
      type: 'executive-summaries',
      filter: 'executiveSummary',
      label: 'forms.common.Criteria.executiveSummary'
    }
  ];

  get showExecutivePlan() {
    return this.executeVisitType == ExecuteVisitType.InstitutionEntries ||
      this.executeVisitType == ExecuteVisitType.FinalReportWithInstitutionEntries;
  }

  selectedNode: ICriterion = null;
  treeControl = new NestedTreeControl<ICriterion>((node) => node.subCriteria);
  dataSource: MatTreeNestedDataSource<ICriterion>;

  isLoadingResults = false;

  showMemberDropdown: boolean;

  readonly hasChild = (_: number, node: ICriterion) =>
    !!node.subCriteria && node.subCriteria.length > 0;

  readonly trackBy = (_: number, node: ICriterion) => node.id;

  constructor(
    private criterionsService: CriterionsService,
    private itimadCriterionsService: ItimadCriterionsService,
    public translateService: TranslateService
  ) {
    super();
  }

  ngOnInit(): void {
  }
  ngOnChanges(changes: SimpleChanges) {
    this.showMemberDropdown = this.executeVisitType == ExecuteVisitType.SessionDetails && this.visitMetaData.role == RolesEnum.TEAM_LEADER;
    if (!this.showMemberDropdown) this.getTreeData();
    this.criterionsService.decisionTaken$
      .pipe(
        filter(_ => !!this.selectedNode),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.selectedNode.evaluate = true;
        // this.updateResultData();
        this.getTreeData();
      });
    this.criterionsService.hasMistake$
      .pipe(
        filter(_ => !!this.selectedNode),
        takeUntil(this.destroy$)
      )
      .subscribe((value) => this.selectedNode.hasError = value);
  }

  memberChanged(member: MemberModel) {
    this.selectedNode = null;
    this.getTreeData(member);
  }
  rowClicked(node: ICriterion) {
    if (this.selectedNode?.id !== node?.id) {
      this.selectedNode = node;
      this.criterionsService.criterion$.next(node);
    }
  }
  getTreeData(member: MemberModel = null) {
    const reviewerId = member?.reviewer?.id;
    const enableDecisionEdit = !reviewerId || reviewerId == this.visitMetaData.reviewerId;

    this.criterionsService.decisionInputs$.next({
      categoriesCompliance: this.categoriesCompliance,
      visitMetaData: this.visitMetaData,
      isMasar: false,
      enableEdit: !this.viewOnly && enableDecisionEdit,
      currentMemberId: member?.id || this.visitMetaData.memberId,
      currentReviewerId: member?.reviewer?.id || this.visitMetaData.reviewerId,
      viewOnly: this.viewOnly,
      executeVisitType: this.executeVisitType,
    });

    this.isLoadingResults = true;
    forkJoin([
      iif(
        () => this.executeVisitType == ExecuteVisitType.SessionDetails,
        this.itimadCriterionsService.getMyCriterionByVisitKey(this.visitMetaData.visitKey, reviewerId || this.visitMetaData.reviewerId),
        this.itimadCriterionsService.getAllCriterionByVisitKey(this.visitMetaData.visitKey)
      ),
      this.criterionsService.getProccess(this.visitMetaData.accreditationKey, this.visitMetaData.eligibilityRequestId)
    ]).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.isLoadingResults = false;
      })
    ).subscribe((result) => {
      this.criterions = result[0];
      this.dataSource = new MatTreeNestedDataSource<ICriterion>();
      this.dataSource.data = this.criterions;
      this.updateResultData();
    });
  }

  private updateResultData() {
    const value = this.criterionsService.results$.getValue();
    value.criterion = this.dataSource.data;
    value.flatCriterion = _cloneDeep(value.criterion);

    let flatten = (children, extractChildren) => Array.prototype.concat.apply(
      children,
      children.map(x => flatten(extractChildren(x) || [], extractChildren))
    );
    let extractChildren = x => x.subCriteria;
    value.flatCriterion = flatten(extractChildren({ subCriteria: value.flatCriterion }), extractChildren)
      .map(x => delete x.subCriteria && x);

    this.criterionsService.results$.next(value);
  }
}
